import { useEffect, useState } from "react";
import { Check, AlertCircle, Bell, Building2, User } from "lucide-react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";

/**
 * Go-live Readiness Board.
 *
 * PUBLIC within the company by design (Owner directive Feb 2026):
 *   ALL logged-in users see the board — including names of laggards.
 *   Clients are excluded server-side (403).
 * Owner/Head can tick manual items + send nudges.
 */
export default function ReadinessPage() {
    const [board, setBoard] = useState(null);
    const [busy, setBusy] = useState(false);
    const [nudgeBusy, setNudgeBusy] = useState({});
    const [tab, setTab] = useState("sites");

    async function load() {
        try {
            const r = await api.get("/readiness/board");
            setBoard(r.data);
        } catch (e) {
            const status = e?.response?.status;
            if (status === 403) {
                setBoard({ forbidden: true });
                return;
            }
            toast.error(apiError(e));
        }
    }
    useEffect(() => { load(); }, []);

    async function tick(row, ticked, note) {
        setBusy(true);
        try {
            await api.post("/readiness/tick", {
                key: row.key,
                site_id: row.site_id || null,
                user_id: row.user_id || null,
                ticked, note: note || null,
            });
            await load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    }

    async function nudge(row) {
        const key = (row.site_id || "") + "|" + (row.user_id || "");
        setNudgeBusy((s) => ({ ...s, [key]: true }));
        try {
            const msg = window.prompt("Nudge message (optional):", "Please complete your outstanding readiness items today.");
            if (msg === null) return;
            await api.post("/readiness/nudge", {
                user_id: row.user_id || null, site_id: row.site_id || null,
                message: msg,
            });
            toast.success("Nudge queued.");
        } catch (e) { toast.error(apiError(e)); }
        finally { setNudgeBusy((s) => ({ ...s, [key]: false })); }
    }

    if (!board) return <div className="p-6 text-sm text-gray-500" data-testid="readiness-loading">Loading readiness board…</div>;
    if (board.forbidden) return (
        <div className="mx-auto max-w-lg px-6 py-16 text-center" data-testid="readiness-forbidden">
            <div className="rounded-2xl border border-gray-200 bg-white p-8">
                <div className="text-lg font-semibold text-[#1E3355]">This board isn't available for your role.</div>
                <p className="text-sm text-gray-500 mt-2">The Readiness Board is company-internal. Please contact your Facility Head if you need to raise an operational item.</p>
            </div>
        </div>
    );

    const pct = board.percent || {};

    function Percent({ label, val }) {
        const color = val >= 90 ? "text-emerald-700 bg-emerald-100" : val >= 60 ? "text-amber-700 bg-amber-100" : "text-red-700 bg-red-100";
        return (
            <div className={`rounded-xl px-4 py-3 ${color}`}>
                <div className="text-[11px] uppercase tracking-wider">{label}</div>
                <div className="text-2xl font-bold">{val}%</div>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6" data-testid="readiness-page">
            <div className="mb-4">
                <h2 className="text-xl font-semibold text-[#1E3355]">Go-live Readiness Board</h2>
                <p className="text-xs text-gray-600 mt-0.5">Company-internal. Public by design — every logged-in user sees who is missing what. Clients are excluded.</p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="readiness-percent">
                <Percent label="Overall" val={pct.overall ?? 0} />
                <Percent label="Sites" val={pct.sites ?? 0} />
                <Percent label="Facility Heads" val={pct.fhs ?? 0} />
                <Percent label="HO" val={pct.ho ?? 0} />
            </div>

            {/* Laggard call-outs */}
            {(board.laggards_by_person?.length > 0 || board.laggards_by_site?.length > 0) && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                    <div className="rounded-xl border border-red-200 bg-red-50/60 p-4" data-testid="laggards-people">
                        <div className="text-sm font-semibold text-red-800 mb-2 flex items-center gap-2">
                            <User className="w-4 h-4" /> People with outstanding items ({board.laggards_by_person.length})
                        </div>
                        <ul className="space-y-2 text-xs">
                            {board.laggards_by_person.map((p) => (
                                <li key={p.user_id} className="flex items-start gap-2" data-testid={`laggard-person-${p.user_id}`}>
                                    <div className="flex-1">
                                        <div className="font-medium text-gray-900">{p.user_name}</div>
                                        <div className="text-gray-600">{p.missing.join(" · ")}</div>
                                    </div>
                                    {board.can_nudge && (
                                        <button
                                            className="text-[11px] px-2 py-0.5 rounded-full bg-[#E02B2B] text-white hover:bg-red-700 inline-flex items-center gap-1"
                                            disabled={nudgeBusy[`|${p.user_id}`]}
                                            onClick={() => nudge({ user_id: p.user_id })}
                                            data-testid={`nudge-person-${p.user_id}`}
                                        >
                                            <Bell className="w-3 h-3" /> Nudge
                                        </button>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4" data-testid="laggards-sites">
                        <div className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-2">
                            <Building2 className="w-4 h-4" /> Sites with gaps ({board.laggards_by_site.length})
                        </div>
                        <ul className="space-y-2 text-xs">
                            {board.laggards_by_site.map((s) => (
                                <li key={s.site_id} data-testid={`laggard-site-${s.site_id}`}>
                                    <div className="font-medium text-gray-900">{s.site_name}</div>
                                    <div className="text-gray-600">{s.missing.join(" · ")}</div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            <div className="flex items-center gap-2 border-b border-gray-200 mb-4">
                {[
                    ["sites", `Sites (${board.site_rows.length})`],
                    ["fhs", `Facility Heads (${board.fh_rows.length})`],
                    ["ho", `HO (${board.ho_rows.length})`],
                ].map(([k, label]) => (
                    <button
                        key={k}
                        onClick={() => setTab(k)}
                        className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${tab === k ? "border-[#E02B2B] text-[#1E3355]" : "border-transparent text-gray-500 hover:text-gray-700"}`}
                        data-testid={`readiness-tab-${k}`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            <div className="fx-table-scroll" data-testid={`readiness-table-${tab}`}>
                <table className="w-full text-sm border-collapse">
                    <thead>
                        <tr className="text-left text-xs text-gray-600 uppercase tracking-wider border-b border-gray-200">
                            <th className="py-2 pr-4">Status</th>
                            <th className="py-2 pr-4">{tab === "sites" ? "Site" : tab === "fhs" ? "Facility Head" : "Scope"}</th>
                            <th className="py-2 pr-4">Item</th>
                            <th className="py-2 pr-4">Note</th>
                            {board.can_tick && <th className="py-2 pr-4">Action</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {(tab === "sites" ? board.site_rows : tab === "fhs" ? board.fh_rows : board.ho_rows).map((r, i) => (
                            <tr key={i} className="border-b border-gray-100" data-testid={`readiness-row-${tab}-${i}`}>
                                <td className="py-2 pr-4">
                                    {r.done ? (
                                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5">
                                            <Check className="w-3 h-3" /> Done
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-700 text-xs px-2 py-0.5">
                                            <AlertCircle className="w-3 h-3" /> Missing
                                        </span>
                                    )}
                                    {r.auto && <span className="ml-1 text-[10px] text-gray-400 uppercase">auto</span>}
                                </td>
                                <td className="py-2 pr-4 text-gray-700">
                                    {tab === "sites" ? r.site_name : tab === "fhs" ? r.user_name : "HO"}
                                </td>
                                <td className="py-2 pr-4">{r.label}</td>
                                <td className="py-2 pr-4 text-xs text-gray-500">{r.note || ""}</td>
                                {board.can_tick && (
                                    <td className="py-2 pr-4">
                                        {!r.auto && (
                                            <button
                                                onClick={() => {
                                                    const note = window.prompt("Note (optional):", r.note || "");
                                                    if (note !== null) tick(r, !r.done, note);
                                                }}
                                                disabled={busy}
                                                className="text-xs px-2 py-0.5 rounded-full bg-[#1E3355] text-white hover:bg-[#15243C]"
                                                data-testid={`readiness-tick-${tab}-${i}`}
                                            >
                                                {r.done ? "Untick" : "Tick"}
                                            </button>
                                        )}
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
